/**
 * The labels of record of the tab "Email to order" of the page "Learn from sales"
 * (`GET /sales-truth/trail`, 4 Nov 2026): how well I2Q reads the inquiry mails that became
 * an F&O sales order. Every score here is a score of I2Q, never of a person (lead decision
 * S3 stays: the tab shows no sales person and no rank).
 *
 * `tests/ste.test.mjs` lints this file with the house rules. The house words are used:
 * "mail" (not "email") and "item" (not "part"); the name of the tab and "Overall accuracy"
 * are the words the product owners chose.
 */

/** The tab "Email to order": the texts the tab prints. */
export const TRAIL_LABELS = {
  areasLabel: "Areas of the trail",
  overview: "Overview",
  volume: "Volume",
  checked: "inquiries checked",
  becameOrder: "became an order",
  strings: "mail strings scored",
  mails: "mails",
  // ste-allow: product-term (the headline the product owners chose)
  overall: "Overall accuracy",
  overallCaption: "Customer, every ordered item and its quantity right.",
  customer: "Customer",
  customerCaption: "I2Q had the customer of the quotation.",
  items: "Items",
  itemsCaption: "Ordered items that I2Q found.",
  qty: "Qty",
  qtyCaption: "Found items with the quoted quantity.",
  steps: "Step by step",
  stepRead: "1 Mail read",
  stepCustomer: "2 Customer",
  stepItemsQuote: "3 Items = quote",
  stepQtyQuote: "4 Qty = quote",
  stepItemsOrder: "5 Items = order",
  trust: "Trust the green tick",
  trustCaption: "Matched items that are on the quotation or the order.",
  single: "Single mail",
  multi: "String of 2 or more mails",
  fullyRightShort: "fully right",
  table: "Results per area",
  colArea: "Area",
  colChecked: "Inquiries checked",
  colOrder: "Became an order",
  colStrings: "Strings scored",
  colOverall: "Overall",
  colCustomer: "Customer",
  colSister: "Customer incl. sister accounts",
  colItems: "Items",
  colQty: "Qty",
  colRead: "Mail read",
  colItemsQuote: "Items = quote",
  colTrust: "Trust",
  colMiss: "Biggest miss reason",
  noMiss: "None",
  filters: "Show strings",
  allStrings: "All",
  notRight: "Not fully right",
  mailString: "Mail string",
  extraction: "Extraction",
  salesOrder: "Sales order",
  orderDate: "Order date",
  customerPo: "Customer PO",
  mailShort: "mail",
  fullyRight: "FULLY RIGHT",
  itemsShort: "Items",
  addedOnOrder: "Added on the order",
  noCustomer: "No customer",
  quotedNote: "This is the quoted customer. The F&O copy has no customer of the sales order.",
  colleagueNotes: "Colleague notes",
  threadLines: "lines from an older mail",
  notShown: "Not shown",
  empty: "No inquiry of this period became a sales order yet.",
  emptyFiltered: "No mail string fits this filter.",
  exportCsv: "Export CSV",
  howTitle: "How it is measured",
  how: "I2Q is checked against the F&O quotation and its sales order. I2Q gets a score for the whole mail string: all mails of one quotation together. F&O is not always right, so a miss can be a mistake in F&O.",
  howTouches:
    "A touch point is a mail of the customer or a reply of Norican. The replies come from Sent Items and from the older mails that a customer mail quotes. The page shows only the time and the direction of a mail, never its text.",
  howReasons:
    "The reasons come from the kept text of the customer mails, the attachments, the item master and the customer master. The first reason of a line is its main reason.",
  touchIn: "In",
  touchOut: "Out",
  quoteSent: "Quote sent",
  quoteSentEstimated: "Quotation made (no sent date in F&O)",
  quoteNotSent: "Quote sent: not known",
  touchBuckets: "Fully right by touch points to the quotation",
  touch1: "1 touch point",
  touch23: "2 to 3",
  touch4: "4 or more",
  invoiceAccount: "Invoice account",
  // The F&O copy has no customer of the sales order yet: the column points to the quotation.
  orderCustomerShort: "Customer: see quotation",
  topReasons: "Top reasons",
  colReason: "Reason",
  colReasonStrings: "Strings",
  showReason: "Show the strings",
  noReasons: "Every string is fully right.",
  howFound: "How I2Q found the customer",
} as const;

/** "4 touch points to the quotation (3 in, 1 out) · 1 after". */
export function touchCountText(before: number, inbound: number, outbound: number, after: number) {
  const noun = before === 1 ? "touch point" : "touch points";
  return `${before} ${noun} to the quotation (${inbound} in, ${outbound} out) · ${after} after`;
}

/** The reasons of a string not fully right (`TrailString.reasons`), items first. */
export const TRAIL_REASON_LABELS = {
  item_added_by_sales: "Item: added by sales, not asked for",
  item_old_number: "Item: old or replaced number",
  item_customer_number: "Item: customer's own number",
  item_in_mail_text: "Item: number in the mail, I2Q missed it",
  item_attachment_only: "Item: only in an attachment",
  item_description_only: "Item: description only, no number",
  item_i2q_unsure: "Item: I2Q was not sure",
  item_unknown: "Item: reason not known",
  customer_do_not_use: "Customer: account not to use",
  customer_sister: "Customer: sister account",
  customer_likely_same: "Customer: probably the same company",
  customer_different: "Customer: other company",
  customer_no_account: "Customer: I2Q had no account",
  customer_several_accounts: "Customer: the sender has several accounts",
  qty_unit_differs: "Qty: another unit",
  qty_read_differs: "Qty: I2Q read another quantity",
  qty_partial_order: "Qty: fewer ordered than quoted",
  qty_changed_on_order: "Qty: changed on the order",
} as const;

/** Why a string is not fully right (the chips of the filter). */
export const TRAIL_TAG_LABELS = {
  no_lines: "No lines read",
  fewer_lines: "Fewer lines than asked",
  missed_lines: "Missed lines",
  wrong_part: "Wrong item",
  sure_but_wrong: "Sure but wrong",
  qty_differs: "Qty differs",
  wrong_customer: "Wrong customer",
  no_customer: "No customer",
} as const;

/** The check of the customer master when the I2Q customer is not the quoted one. */
export const TRAIL_CUSTOMER_LABELS = {
  do_not_use: "⛔ Do not use",
  // amber: the account is a sister of the quoted one, not the quoted one itself
  sister: "🟡 Sister account",
  likely_same: "🟡 Probably the same company",
  different: "❌ Other company",
  no_account: "⚠️ No account",
} as const;

/** How a mail belongs to the string. */
export const TRAIL_RELATION_LABELS = {
  inquiry: "Inquiry",
  first_mail: "First mail",
  reply: "Reply",
  copy: "Copy",
  copy_of: "Copy of",
  other_mailbox: "Other mailbox",
  same_inquiry: "Same inquiry",
  same_inquiry_as: "Same inquiry as",
} as const;

/** "Draft had 2 of 3 ordered lines." */
export function draftHadText(found: number, total: number): string {
  return `Draft had ${found} of ${total} ordered lines.`;
}
